import React, { useState, useEffect } from "react";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { Clock, CheckCircle2, XCircle, Filter, RefreshCw, TrendingUp, TrendingDown, AlertCircle, Lock, ArrowLeftRight } from "lucide-react";
import { BaseCard, PrimaryButton, SecondaryButton, DangerButton, GoldButton, Badge, Modal } from "@/components/ui/DesignSystem";

export default function ApprovalCenterAdminPage() {
  const [activeTab, setActiveTab] = useState("PENDING"); // PENDING, EXECUTED, REJECTED, ALL
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [error, setError] = useState("");

  // Selected Request Modal
  const [selectedReq, setSelectedReq] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await adminApi.get(`/admin/approvals?status=${activeTab}`);
      setRequests(res.data?.requests || []);
      setPendingCount(res.data?.pending_count || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || "تعذر تحميل الطلبات");
      toast.error("فشل تحميل طلبات مركز الموافقات");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      setLoading(true);
      const res = await adminApi.post(`/admin/approvals/${id}/approve`);
      toast.success(res.data?.message || "تم الموافقة والتنفيذ بنجاح!");
      setSelectedReq(null);
      fetchRequests();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التنفيذ");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async (id) => {
    try {
      setLoading(true);
      const res = await adminApi.post(`/admin/approvals/${id}/reject`, { reason: rejectReason });
      toast.success(res.data?.message || "تم الرفض وإلغاء حجز المبلغ");
      setSelectedReq(null);
      setRejectReason("");
      fetchRequests();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل الرفض");
    } finally {
      setLoading(false);
    }
  };

  const requestChanges = async (id) => {
    try {
      setLoading(true);
      await adminApi.post(`/admin/approvals/${id}/request-changes`, { reason: rejectReason });
      toast.success('تم إرسال طلب التعديلات للمستخدم');
      setSelectedReq(null); setRejectReason(''); fetchRequests();
    } catch (e) { toast.error(e?.response?.data?.detail || 'تعذر تحديث حالة الطلب'); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 fade-up">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b pb-4">
        <div>
          <h2 className="section-title text-2xl font-black">مركز الموافقات المالية (Approval Center)</h2>
          <p className="text-xs text-[var(--c-muted)] mt-1">
            مراجعة واتخاذ القرار لجميع طلبات الإيداع والسحب والتحويلات المالية الحساسة.
          </p>
        </div>
        <SecondaryButton onClick={fetchRequests} size="sm">
          <RefreshCw className="w-4 h-4" /> تحديث الطلبات ({pendingCount} معلق)
        </SecondaryButton>
      </div>

      {/* Tabs Filter */}
      <div className="mb-tab-list mb-surface p-2 shadow-sm">
        {[
          { id: "PENDING", label: `الطلبات المعلقة (${pendingCount})`, icon: Clock },
          { id: "CHANGES_REQUESTED", label: "تعديلات مطلوبة", icon: AlertCircle },
          { id: "EXECUTED", label: "الطلبات المنفذة والموافق عليها", icon: CheckCircle2 },
          { id: "REJECTED", label: "الطلبات المرفوضة", icon: XCircle },
          { id: "CANCELLED", label: "الملغاة", icon: XCircle },
          { id: "EXPIRED", label: "المنتهية", icon: Clock },
          { id: "ALL", label: "جميع الطلبات", icon: Filter }
        ].map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex shrink-0 min-h-10 items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap ${
                active ? "bg-[var(--c-navy)] text-white shadow-sm" : "text-[var(--c-muted)] hover:bg-slate-100"
              }`}
            >
              <Icon className={`w-4 h-4 ${active ? "text-[var(--c-gold)]" : ""}`} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Requests List */}
      <BaseCard className="space-y-4">
        {error ? (
          <div className="text-center py-10 space-y-3" role="alert">
            <AlertCircle className="w-10 h-10 mx-auto text-red-500" />
            <p className="text-sm font-bold text-red-700">{error}</p>
            <SecondaryButton onClick={fetchRequests}><RefreshCw className="w-4 h-4" /> إعادة المحاولة</SecondaryButton>
          </div>
        ) : loading ? (
          <div className="text-center py-12 text-sm text-[var(--c-muted)]">جاري تحميل الطلبات…</div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12 text-xs text-[var(--c-muted)] space-y-2">
            <Clock className="w-10 h-10 mx-auto text-slate-300" />
            <p>لا توجد طلبات موافقات في هذه الفئة.</p>
          </div>
        ) : (
          <div className="space-y-3 font-num">
            {requests.map((r) => (
              <div key={r.id} className="p-4 bg-slate-50 border border-slate-200 rounded-2xl flex flex-wrap items-center justify-between gap-4 hover:border-slate-300 transition">
                <div className="flex min-w-0 items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-white ${
                    r.type === "deposit" ? "bg-emerald-600" : r.type === "BOX_EMERGENCY_UNLOCK" ? "bg-amber-600" : "bg-blue-600"
                  }`}>
                    {r.type === "deposit" ? <TrendingUp className="w-5 h-5" /> : r.type === "BOX_EMERGENCY_UNLOCK" ? <Lock className="w-5 h-5" /> : r.type === "transfer" ? <ArrowLeftRight className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                  </div>
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate font-bold text-xs text-[var(--c-navy)]">{r.type === 'transfer' ? `تحويل من ${r.display_name} إلى ${r.transfer_recipient?.display_name || 'مستخدم محذوف'}` : `${r.display_name} (${r.username})`}</span>
                      <span className="font-mono text-[10px] text-slate-500">#{r.id}</span>
                    </div>
                    <div className="text-[10px] text-[var(--c-muted)] mt-0.5">
                      نوع الطلب: <b>{r.type === "deposit" ? "إيداع في بنك العائلة" : r.type === "transfer" ? `تحويل مالي من ${r.username} إلى ${r.transfer_recipient?.username || 'مستخدم محذوف'}` : r.type === "BOX_EMERGENCY_UNLOCK" ? `فتح طارئ: ${r.box_name_snapshot}` : "سحب أموال"}</b> | الوقت: {r.created_at?.slice(0, 16)}
                    </div>
                  </div>
                </div>

                <div className="flex w-full items-center justify-between gap-4 sm:w-auto">
                  <div className="text-left font-black text-sm text-[var(--c-navy)]">
                    {r.type === "BOX_EMERGENCY_UNLOCK" ? "بدون حركة مالية" : `${Number(r.amount || 0).toLocaleString()} ${r.currency}`}
                  </div>

                  <PrimaryButton onClick={() => setSelectedReq(r)} size="sm">
                    عرض وتأكيد القرار
                  </PrimaryButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </BaseCard>

      {/* REQUEST DETAIL & DECISION MODAL */}
      <Modal isOpen={Boolean(selectedReq)} onClose={() => setSelectedReq(null)} title={`مراجعة الطلب ${selectedReq?.id}`}>
        {selectedReq && (
          <div className="space-y-4 py-2 text-xs font-medium">
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-2 font-num">
              <div className="flex justify-between border-b pb-1.5">
                <span className="text-[var(--c-muted)]">صاحب الطلب:</span>
                <span className="font-bold text-[var(--c-navy)]">{selectedReq.display_name} ({selectedReq.username})</span>
              </div>
              <div className="flex justify-between border-b pb-1.5">
                <span className="text-[var(--c-muted)]">المبلغ المطلق:</span>
                <span className="font-black text-sm text-[var(--c-navy)]">{selectedReq.type === "BOX_EMERGENCY_UNLOCK" ? "لا يوجد مبلغ" : `${selectedReq.amount?.toLocaleString()} ${selectedReq.currency}`}</span>
              </div>
              <div className="flex justify-between border-b pb-1.5">
                <span className="text-[var(--c-muted)]">نوع العملية:</span>
                <span className="font-bold">{selectedReq.type === "deposit" ? "إيداع (من أموالي + إلى بنك العائلة)" : selectedReq.type === "transfer" ? `تحويل مالي من ${selectedReq.display_name} إلى ${selectedReq.transfer_recipient?.display_name || 'مستخدم محذوف'}` : selectedReq.type === "BOX_EMERGENCY_UNLOCK" ? `فتح طارئ للصندوق: ${selectedReq.box_name_snapshot}` : "سحب"}</span>
              </div>
              {selectedReq.type === 'transfer' && <div className="flex justify-between border-b pb-1.5"><span className="text-[var(--c-muted)]">المستلم:</span><span className="font-bold text-[var(--c-navy)]">{selectedReq.transfer_recipient?.display_name || 'مستخدم محذوف'} ({selectedReq.transfer_recipient?.username || '—'})</span></div>}
              <div className="flex justify-between">
                <span className="text-[var(--c-muted)]">حالة الطلب الحالية:</span>
                <Badge variant={selectedReq.status === "PENDING" ? "warning" : "success"}>{selectedReq.status}</Badge>
              </div>
            </div>

            {selectedReq.type === "BOX_EMERGENCY_UNLOCK" && <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-950"><b>سبب الطلب</b><p className="mt-2">{selectedReq.reason}</p><p className="mt-2 text-[10px]">وضع القفل: {selectedReq.lock_information?.lock_mode} {selectedReq.lock_information?.locked_until ? `حتى ${selectedReq.lock_information.locked_until}` : ""}</p></div>}

            {selectedReq.status === "PENDING" && (
              <div className="space-y-3 pt-2">
                <div>
                  <label className="block text-xs font-bold text-[var(--c-navy)] mb-1">سبب الرفض (في حال اختيار الرفض)</label>
                  <input
                    type="text"
                    className="w-full p-2.5 border rounded-xl"
                    placeholder="سبب الرفض الإلزامي..."
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <PrimaryButton loading={loading} onClick={() => handleApprove(selectedReq.id)} fullWidth>
                    <CheckCircle2 className="w-4 h-4" /> {selectedReq.type === "BOX_EMERGENCY_UNLOCK" ? "موافقة وفتح الصندوق" : "موافقة وتنفيذ مالي"}
                  </PrimaryButton>
                  <DangerButton loading={loading} onClick={() => handleReject(selectedReq.id)} fullWidth>
                    <XCircle className="w-4 h-4" /> رفض الطلب
                  </DangerButton>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <GoldButton loading={loading} onClick={() => requestChanges(selectedReq.id)} fullWidth>طلب تعديلات</GoldButton>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
